import { useCallback, useEffect, useState, useMemo } from "react";
import {
  Button,
  Dropdown,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import Table from "react-bootstrap/Table";
import "./itemlisting.scss";
import threedotvertical from "../../../assets/images/three-dots-vertical.svg";
import viewicon from "../../../assets/images/view-icon.svg";
import editicon from "../../../assets/images/edit-icon.svg";
import duplicateicon from "../../../assets/images/duplicate-icon.svg";
import duplicatePlusIcon from "../../../assets/images/duplicate-plus-icon.svg";
import deleteicon from "../../../assets/images/delete-icon.svg";
import { ReactSVG } from "react-svg";
import useInventory from "./../../../Hooks/useInventory";
import { useNavigate } from "react-router";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  setSelectedItem,
  selectedInventoryItem,
} from "../../../redux/inventory/inventorySlice";
import {
  dashboardColors,
  itemPagesettingName,
  toggleClass,
} from "../../../utils/commonUtils";
import Loading from "../../../components/LoadingPage/Loading";
import { DeleteConfirmModel } from "./popUpModels/deleteConfModel";
import { selectCurrentUserId } from "../../../redux/auth/authSlice";
import { DuplicateItemModel } from "./popUpModels/duplicateItemModel";
import { PrintBarcodePopUp } from "../InventoryProfile/printBarcodePopUp/printBarcodePopUp";
import moment from "moment";
import ItemListingTitle from "../../../components/itemListingTitle/itemListingTitle";
import useSettings from "../../../Hooks/useSettings";
import {
  useModulePermissions,
  PermissionModule,
  PermissionIdentifier,
} from "../../../utils/permissionUtils";

const columns = [
  { key: "rxSRXId", labelKey: "SrxBarcode", defaultLabel: "rXSRX ID" },
  { key: "stock", labelKey: "Stock", defaultLabel: "Stock" },
  { key: "rxType", labelKey: "rxTypeCode", defaultLabel: "Item Type" },
  { key: "rxName", labelKey: "ItemName", defaultLabel: "Item Name" },
  {
    key: "manufacturer",
    labelKey: "Manufacturer",
    defaultLabel: "Manufacturer",
  },
  { key: "ndcCode", labelKey: "Ndc", defaultLabel: "NDC" },
  { key: "lotNo", labelKey: "Lot", defaultLabel: "Lot" },
  {
    key: "expiryDate",
    labelKey: "ExpirationDate",
    defaultLabel: "Expiry Date",
  },
  { key: "drugName", labelKey: "ItemGroup", defaultLabel: "Rx Group" },
  { key: "cvx", labelKey: "CvxModel", defaultLabel: "CVX" },
  { key: "isRecalled", labelKey: "Recalled", defaultLabel: "Recalled" },
  { key: "isActive", labelKey: "Active", defaultLabel: "Status" },
  { key: "createdOnUtc", labelKey: "CreatedOnUtc", defaultLabel: "Created On" },
];

const ItemListing = () => {
  const currentUserId = useAppSelector(selectCurrentUserId);
  const defaultSelectedInv = useAppSelector(selectedInventoryItem);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [deleteInventoryErr, setDeleteInventoryErr] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [itemsList, setItemsList] = useState([]);
  const [currentItem, setCurrentItem] = useState<any>();
  const [showBarcodeModal, setShowBarcodeModal] = useState(false);
  const [selectedItemRow, setSelectedItemRow] = useState<any>();
  const [defaultBarcodes, setDefaultBarcodes] = useState<any>();
  const [sortColumn, setSortColumn] = useState("rxName");
  const [sortDirection, setSortDirection] = useState("asc");

  const defaultFilter = useMemo(() => ({
    searchText: "",
    active: true,
    sortColumn: "rxName",
    sortDirection: "asc",
  }), []);

  const [searchFieldVal, setSearchFieldVal] = useState(defaultFilter);
  const [stockColorScheme, setStockColorScheme] = useState<Record<string, string>>({});
  const [selectedRowId, setSelectedRowId] = useState<string>("");
  const [settingsList, setSettingsList] = useState<any>([]);
  const [tooltipText, setTooltipText] = useState("Copy to clipboard");
  const { getSettingsAPI, getSettingsLoading } = useSettings();
  const {
    getInventoryItemsList,
    inventoryItemListLoading,
    getBarcodesAPI,
    deleteInventoryItemAPI,
    deleteInventoryItemLoading,
    barcodePrintAPI
  } = useInventory();

  useEffect(() => getSettings(), []);

  useEffect(() => getListItems(), [searchFieldVal]);

  useEffect(() => getBarcodeList(), [selectedItemRow?.id]);

  useEffect(() => setSelectedRowId(defaultSelectedInv?.rxSRXId ?? ""), [defaultSelectedInv]);

  const toggleBarcodeModal = () => {
    toggleClass();
    setShowBarcodeModal(!showBarcodeModal);
  };

  const getBarcodeList = () => {
    if (selectedItemRow?.id) {
      getBarcodesAPI(selectedItemRow?.id).then((res: any) => {
        if (res) {
          setDefaultBarcodes(res.find((x: any) => x.isDefault));
        }
      });
    }
  };

  const getSettings = () => {
    getSettingsAPI({ searchText: "" })
      .then((res: any) => {
        const settingsResult = res?.find(
          (option: any) =>
            option.settingName.toLowerCase() ===
            itemPagesettingName.toLowerCase()
        );
        setSettingsList(
          settingsResult
            ? JSON.parse(settingsResult?.settingValue?.toString())
            : []
        );
      })
      .catch();
  };

  const printBarcode = (label: string, barcode: string) => {
    barcodePrintAPI({
      lableId: label,
      id: barcode,
    }).then((res: any) => {
      window.open(res.data.result, "_blank", "noopener,noreferrer");
    });
  };

  const navigateToView = (item: any) => {
    dispatch(setSelectedItem(item));
    navigate("/view-inventory");
  };

  const toggleModal = (item: any) => {
    setCurrentItem(item);
    toggleClass();
    setShowModal(!showModal);
    setDeleteInventoryErr("");
  };

  const toggleDuplicateModal = () => {
    toggleClass();
    setShowDuplicateModal(!showDuplicateModal);
  };

  const handleSort = useCallback((columnName: string) => {
    const newSortDirection = sortColumn === columnName ? (sortDirection === "asc" ? "desc" : "asc") : "asc";
    setSortColumn(columnName);
    setSortDirection(newSortDirection);
    setSearchFieldVal(prev => ({
      ...prev,
      sortColumn: columnName,
      sortDirection: newSortDirection,
    }));
  }, [sortColumn, sortDirection]);

  const getListItems = useCallback(() => {
    getInventoryItemsList(searchFieldVal).then((res: any) => {
      if (res) {
        setItemsList(res);
      } else {
        setItemsList([]);
      }
    });
  }, [searchFieldVal]);

  const editItem = (item: any) => {
    dispatch(setSelectedItem(item));
    navigate("/edit-inventory");
  };

  const onClose = () => {
    toggleClass();
    setShowModal(!showModal);
    setDeleteInventoryErr("");
  };

  const deleteItem = async () => {
    const res = await deleteInventoryItemAPI({
      id: currentItem?.id,
      deletedBy: currentUserId,
    });
    if (res?.errorMsg) {
      setDeleteInventoryErr(res?.errorMsg);
    } else {
      onClose();
      getListItems();
    }
  };

  const getStockChipColor = (currentStock: string): string => {
    return stockColorScheme?.[currentStock] ?? "";
  };

  useEffect(() => {
    if (itemsList.length > 0) {
      const stockSet = new Set(
        itemsList
          .filter((item: any) => item?.stock?.stockName)
          ?.map((item: any) => item.stock.stockName)
      );

      const sortedStocks = Array.from(stockSet)?.sort((a, b) =>
        a.localeCompare(b)
      );
      const stockColorMap: Record<string, string> = {};

      sortedStocks?.forEach((stock, index) => {
        const color = dashboardColors[index % dashboardColors.length];
        stockColorMap[stock] = color;
      });

      setStockColorScheme(stockColorMap);
    }
  }, [itemsList]);

  const handleRowSelection = (currInv: string) => {
    if (!currInv) return;
    setSelectedRowId((prevState) => (prevState === currInv ? "" : currInv));
  };

  const renderFilterIcon = useCallback(
    (columnName: string) => {
      if (sortColumn === columnName) {
        return sortDirection === "asc" ? "↑" : "↓";
      }
    },
    [sortColumn, sortDirection]
  );

  const handleCopy = (textToCopy: string) => {
    if (!textToCopy) return;
    navigator.clipboard
      .writeText(textToCopy)
      .then(() => {
        setTooltipText("Copied!");
        setTimeout(() => setTooltipText("Copy to clipboard"), 1500);
      })
      .catch(() => {
        setTooltipText("Failed to copy");
      });
  };

  const renderTooltip = (props: any) => (
    <Tooltip id="copy-tooltip" {...props} className="copy-tooltip">
      {tooltipText}
    </Tooltip>
  );

  const isPermitted = useModulePermissions(PermissionModule.RX);
  const isAddPermitted = isPermitted(PermissionIdentifier.ADD_INVENTORY_ITEM);
  const isEditPermitted = isPermitted(PermissionIdentifier.EDIT_INVENTORY_ITEM);
  const isDeletePermitted = isPermitted(PermissionIdentifier.DELETE_INVENTORY_ITEM);
  const isDuplicatePermitted = isPermitted(PermissionIdentifier.DUPLICATE_INVENTORY_ITEM);

  return (
    <div className="item-listing-wrapper">
      {inventoryItemListLoading || deleteInventoryItemLoading || getSettingsLoading ? (<Loading />) : null}
      <ItemListingTitle
        title={"Items"}
        buttonTitle="Add Item"
        href="/create-inventory"
        disRecords={itemsList.length || 0}
        totalRecords={itemsList.length || 0}
        searchVal={searchFieldVal}
        setSearchVal={setSearchFieldVal}
        defaultFilter={defaultFilter}
        isAddPermitted={isAddPermitted}
      />
      <div className="listing-table-wrapper">
        <Table responsive="md" hover={true}>
          <thead>
            {!getSettingsLoading && (
              <tr>
                {columns.map(({ key, labelKey, defaultLabel }) => (
                  <th key={key} onClick={() => handleSort(key)} className="cursor-pointer">
                    {settingsList?.ItemLabels?.[labelKey] ?? defaultLabel}{" "}
                    {renderFilterIcon(key)}
                  </th>
                ))}
                <th>Action</th>
              </tr>
            )}
          </thead>
          <tbody>
            <>
              {itemsList?.map((x: any) => (
                <tr
                  key={x.id}
                  className={`${selectedRowId === x.rxSRXId ? "selected" : ""}`}
                >
                  <td onClick={() => handleRowSelection(x?.rxSRXId)}>
                    <div className="barcode-info">
                      <span
                        onClick={() => navigateToView(x)}
                        className="clickable-text"
                        title={x.rxSRXId}
                      >
                        {x.rxSRXId}
                      </span>
                      <Button
                        onClick={() => handleCopy(x.rxSRXId)}
                        className="copy-btn"
                        variant="link"
                        title="Copy to Clipboard"
                      >
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip}
                          delay={{ show: 250, hide: 400 }}
                        >
                          <ReactSVG
                            className="svg-box"
                            wrapper="span"
                            src={duplicateicon}
                            style={{ width: "auto" }}
                          />
                        </OverlayTrigger>
                      </Button>
                    </div>
                  </td>
                  <td onClick={() => handleRowSelection(x?.rxSRXId)}>
                    <span
                      className="stock-item"
                      style={{
                        borderColor: `${getStockChipColor(x.stock.stockName)}`,
                        color: `${getStockChipColor(x.stock.stockName)}`,
                      }}
                    >
                      {x.stock.stockName}
                    </span>
                  </td>
                  <td onClick={() => handleRowSelection(x?.rxSRXId)}>
                    <span title={x.rxType?.rxTypeCode}>{x.rxType?.rxTypeCode}</span>
                  </td>
                  <td onClick={() => handleRowSelection(x?.rxSRXId)}>
                    <span title={x.rxName}>{x.rxName}</span>
                  </td>
                  <td onClick={() => handleRowSelection(x?.rxSRXId)}>
                    <span title={x.manufacturer}>{x.manufacturer}</span>
                  </td>
                  <td onClick={() => handleRowSelection(x?.rxSRXId)}>
                    <span title={x.ndcCode}>{x.ndcCode}</span>
                  </td>
                  <td onClick={() => handleRowSelection(x?.rxSRXId)}>
                    <span title={x.lotNo}>{x.lotNo}</span>
                  </td>
                  <td
                    onClick={() => handleRowSelection(x?.rxSRXId)}
                    className={
                      (!x.isNeverExpire &&
                        moment(x.expiryDate).isBefore(moment(), "day")) ||
                        (x.expiryDate === null && !x.isNeverExpire)
                        ? "expired"
                        : ""
                    }
                  >
                    <span>
                      {x.isNeverExpire
                        ? "NE"
                        : x.expiryDate === null && !x.isNeverExpire
                          ? "NA"
                          : moment(x.expiryDate).format("MM/DD/YYYY")}
                    </span>
                  </td>
                  <td onClick={() => handleRowSelection(x?.rxSRXId)}>
                    <span title={x.drugName}>{x.drugName}</span>
                  </td>
                  <td onClick={() => handleRowSelection(x?.rxSRXId)}>
                    <span title={x.cvx}>{x.cvx}</span>
                  </td>
                  <td
                    onClick={() => handleRowSelection(x?.rxSRXId)}
                    className={`${x.isRecalled ? "expired" : ""}`}
                  >
                    <span>{x.isRecalled ? "Recalled" : ""}</span>
                  </td>
                  <td onClick={() => handleRowSelection(x?.rxSRXId)}>
                    <span
                      className={
                        "status-circle " + (x.isActive ? "active" : "inactive")
                      }
                    >
                      {x.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td onClick={() => handleRowSelection(x?.rxSRXId)}>
                    <span title={x.createdOnUtc}>
                      {moment(x.createdOnUtc).format("MM/DD/YYYY")}
                    </span>
                  </td>
                  <td>
                    <Dropdown>
                      <Dropdown.Toggle variant="link" id="dropdown-basic">
                        <ReactSVG
                          className="svg-box"
                          wrapper="span"
                          src={threedotvertical}
                        />
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item
                          onClick={() => {
                            navigateToView(x);
                          }}
                        >
                          <ReactSVG
                            className="svg-box"
                            wrapper="span"
                            src={viewicon}
                          />
                          View
                        </Dropdown.Item>
                        {isEditPermitted && (
                          <Dropdown.Item
                            onClick={() => {
                              editItem(x);
                            }}
                          >
                            <ReactSVG
                              className="svg-box"
                              wrapper="span"
                              src={editicon}
                            />
                            Edit
                          </Dropdown.Item>
                        )}
                        {isDeletePermitted && (
                          <Dropdown.Item
                            onClick={() => {
                              toggleModal(x);
                            }}
                          >
                            <ReactSVG
                              className="svg-box"
                              wrapper="span"
                              src={deleteicon}
                            />
                            Delete
                          </Dropdown.Item>
                        )}
                        {isDuplicatePermitted && (
                          <Dropdown.Item
                            onClick={() => {
                              setCurrentItem(x);
                              toggleDuplicateModal();
                            }}
                          >
                            <ReactSVG
                              className="svg-box"
                              wrapper="span"
                              src={duplicatePlusIcon}
                            />
                            Duplicate
                          </Dropdown.Item>
                        )}

                        {/* <Dropdown.Item
                          onClick={() => {
                            setSelectedItemRow(x);
                            toggleBarcodeModal();
                          }}
                        >
                          <ReactSVG
                            className="svg-box"
                            wrapper="span"
                            src={printicon}
                          />
                          Print Barcode
                        </Dropdown.Item> */}
                      </Dropdown.Menu>
                    </Dropdown>
                  </td>
                </tr>
              ))}
            </>
          </tbody>
        </Table>
      </div>
      <PrintBarcodePopUp
        showBarcodeModal={showBarcodeModal}
        toggleBarcodeModal={toggleBarcodeModal}
        printBarcode={printBarcode}
        defaultBarcodes={defaultBarcodes}
      />
      <DuplicateItemModel
        selectedItem={currentItem}
        toggleDuplicateModal={toggleDuplicateModal}
        showModal={showDuplicateModal}
        reloadListing={getListItems}
      />
      <DeleteConfirmModel
        showModal={showModal}
        onClose={onClose}
        item={currentItem}
        deleteItem={deleteItem}
        errorMsg={deleteInventoryErr}
      />
    </div>
  );
};

export default ItemListing;
